/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */
import { css, jsx } from '@emotion/react'

import type { WithEuiThemeProps } from '@elastic/eui'
import { EuiTitle, EuiText, EuiFlexItem, EuiFlexGroup, EuiSpacer, withEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type {
  AnyResourceInfo,
  RegionId,
  SliderInstanceType,
  StackDeployment,
} from '@modules/ui-types'
import { DocLink } from '@modules/doclinks'

import { isStopped } from '@/lib/stackDeployments/selectors/configurationChanges'
import {
  isSliderPlanActive,
  getResourceVersion,
  getPlanInfo,
} from '@/lib/stackDeployments/selectors/fundamentals'
import { countInstances } from '@/lib/stackDeployments/selectors/instances'
import { isHiddenResource } from '@/lib/stackDeployments/selectors/metadata'
import { isFleetServerAvailable } from '@/lib/stackDeployments/fleet'
import { getSliderDocLink } from '@/lib/sliders/docLinks'
import { getSliderDefinition } from '@/lib/sliders/definitions'

import SliderAppLinks from '../SliderAppLinks'
import NodesVisualization from '../../StackDeployments/StackDeploymentNodesVisualization'
import ResourceComments from '../../ResourceComments'
import DeploymentLockGate from '../../DeploymentLockGate'
import { getConfigForKey } from '../../../store'

import ApmIntroNotification from './ApmIntroNotification'
import DeleteResource from './DeleteResource'
import RestartResource from './RestartResource'
import SSOPortOverrideNotification from './SSOPortOverrideNotification'

import type { FunctionComponent } from 'react'

export interface Props {
  deployment: StackDeployment
  resource: AnyResourceInfo
  regionId: RegionId
  sliderInstanceType: SliderInstanceType
  showNativeMemoryPressure: boolean
  hideDelete: boolean
}

interface PropsWithTheme extends Props, WithEuiThemeProps {}

const OverviewEnabled: FunctionComponent<PropsWithTheme> = ({
  deployment,
  resource,
  regionId,
  sliderInstanceType,
  showNativeMemoryPressure,
  hideDelete,
  theme,
}) => {
  const plan = getPlanInfo({ resource })
  const version = getResourceVersion({ resource })

  const { messages } = getSliderDefinition({ sliderInstanceType, version })
  const docLink = getSliderDocLink({ sliderInstanceType, version })

  const active = isSliderPlanActive(plan, sliderInstanceType)
  const stopped = isStopped({ resource })
  const hidden = isHiddenResource({ resource })
  const { totalReported } = countInstances({ resource })

  const isUserConsole = getConfigForKey(`APP_NAME`) === `userconsole`
  const isApm = sliderInstanceType === `apm`

  const hasPendingPlan = getPlanInfo({ resource, state: 'pending' })

  const managementButtonsContainerStyle = css({
    border: `1px solid ${theme.euiTheme.colors.lightShade}`,
    padding: theme.euiTheme.size.m,
  })

  const managementButtonsStyle = css({
    minWidth: '15rem',
  })

  const managementButtonsLegendStyle = css({
    width: 'auto',
    marginBottom: 0,
  })

  return (
    <div>
      <EuiFlexGroup alignItems='flexStart'>
        <EuiFlexItem>
          <EuiFlexGroup direction='column'>
            <EuiFlexItem>
              <EuiText grow={false}>
                <FormattedMessage
                  id='slider-description.message'
                  defaultMessage='{description} {docLink}'
                  values={{
                    description: <FormattedMessage {...messages.description} />,
                    docLink: docLink && (
                      <DocLink link={docLink}>
                        <FormattedMessage
                          id='slider-description.link'
                          defaultMessage='Learn more'
                        />
                        .
                      </DocLink>
                    ),
                  }}
                />
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem>
              {isApm && !isFleetServerAvailable({ version }) && (
                <ApmIntroNotification deployment={deployment} />
              )}
              <SSOPortOverrideNotification isDismissable={false} deployment={deployment} />

              <SliderAppLinks
                deployment={deployment}
                resource={resource}
                sliderInstanceType={sliderInstanceType}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
        {!hasPendingPlan && !hidden && (
          <DeploymentLockGate>{renderManagementButtons()}</DeploymentLockGate>
        )}
      </EuiFlexGroup>

      <EuiSpacer size='xxl' />

      {totalReported === 0 || (
        <NodesVisualization
          title={
            <EuiTitle size='s'>
              <h3>
                <FormattedMessage id='deployment-info.instances' defaultMessage='Instances' />
              </h3>
            </EuiTitle>
          }
          deployment={deployment}
          showNativeMemoryPressure={showNativeMemoryPressure}
          sliderInstanceType={sliderInstanceType}
        />
      )}

      <ResourceComments
        spacerBefore={true}
        resourceType={sliderInstanceType}
        regionId={regionId}
        resourceId={resource.id}
      />
    </div>
  )

  function renderManagementButtons() {
    const buttonsToRender: JSX.Element[] = []

    if (active && !stopped) {
      buttonsToRender.push(
        <div key='restartResource'>
          <RestartResource
            deployment={deployment}
            resource={resource}
            sliderInstanceType={sliderInstanceType}
          />
        </div>,
      )
    }

    // userconsole users only get to see the above button to force restart their deployment
    if (!isUserConsole) {
      if (!hideDelete) {
        buttonsToRender.push(
          <div key='deleteResource'>
            <DeleteResource
              deployment={deployment}
              resource={resource}
              sliderInstanceType={sliderInstanceType}
            />
          </div>,
        )
      }
    }

    if (buttonsToRender.length === 0) {
      return null
    }

    return (
      <EuiFlexItem grow={false} css={managementButtonsStyle}>
        <fieldset data-test-id='slider-management-panel' css={managementButtonsContainerStyle}>
          <legend css={managementButtonsLegendStyle}>
            <FormattedMessage
              id='slider-overview.legend'
              defaultMessage='{prettyName} Management'
              values={{ prettyName: <FormattedMessage {...messages.prettyName} /> }}
            />
          </legend>
          {buttonsToRender}
        </fieldset>
      </EuiFlexItem>
    )
  }
}

export default withEuiTheme(OverviewEnabled)
